import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppInfoService } from 'core/app-info/app-info.service';
import {
  countriesWithProvince, countriesWithStateOrProvinceField,
  countriesWithPostalCodeField,
  countriesWithCountryField
} from 'core/app-info/country-specifics.enum';
import { UtilsService } from 'core/utils/utils.service';
import { SelectedAccountManagerService } from 'core/selected-account-manager/selected-account-manager.service';
import { QuoteDetailsService } from 'src/app/modules/pos/quotes/services/quote-details.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-accounts-advanced-search',
  templateUrl: './accounts-advanced-search.component.html',
  styleUrls: ['./accounts-advanced-search.component.scss'],
  standalone: false
})
export class AccountsAdvancedSearchComponent implements OnInit {

  appStrings = this.appInfoService.appStrings;
  templateValuesUSStates: any[];
  templateValuesCountries: any[];
  allStates = {
    stateName: 'All',
    stateId: 0
  };
  allCountries = {
    Value: 'All',
    Key: 0
  };
  formFields = {
    accountManager: '0',
    Name: '',
    BPNumber: '',
    Contact: '',
    ChildName1: '',
    priceMatrix: '',
    city: '',
    state: 0,
    postalCode: '',
    country: 0,
    filterByInterval: 'all',
    lnStatus: 0,
    leads: true,
    accounts: true,
    delivery: true
  };

  formHasValue = false;
  searchForm = this.fb.group(this.formFields);
  private requiredFilters = [];
  // Country code is not available on offline electron
  // For offline, we'll display all the fields required for US
  countryCode = this.appInfoService.userData.UserInfo.CountryCode || 'US';
  stateOrProvince;
  displayStateOrProvinceField: boolean;
  displayPostalCodeField: boolean;
  displayCountryField: boolean;
  maxlengthZipCode: number;
  isMobile: boolean;
  amList: AccountManager[];
  accountManagerOptions: { value: string, label: string }[] = [];
  allAccountManagerOption: AccountManager = {
    id: '0',
    name: 'All',
    employeeId: '0',
    showOemSku: false
  };
  defaultAccountManager: AccountManager = this.allAccountManagerOption;
  isAccountManager: boolean;
  intervalOptions: { value: string, label: string }[] = [];
  lnStatusOptions: { value: number, label: string }[] = [];
  isOnline: boolean;
  stateOptions: { value: number, label: string }[] = [];
  countryOptions: { value: number, label: string }[] = [];
  private suppressRequiredFilterToggle = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AccountsAdvancedSearchComponent>,
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private utilsService: UtilsService,
    private quoteDetailsService: QuoteDetailsService,
    public deviceService: DeviceDetectorService,
    private cdr: ChangeDetectorRef,
    private selectedAccountManagerService: SelectedAccountManagerService
  ) { }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    // Determine what fields to display for the search menu, based on the country code
    this.displayStateOrProvinceField = countriesWithStateOrProvinceField[this.countryCode];
    this.displayPostalCodeField = countriesWithPostalCodeField[this.countryCode];
    this.displayCountryField = countriesWithCountryField[this.countryCode];
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.intervalOptions = this.data.intervalOptions || [];
    this.lnStatusOptions = this.data.lnStatusOptions || [];
    this.isOnline = this.data.isOnline;

    if (!this.isAccountManager) {
      this.setInitialAccountManagerValue();
      this.getAccountManagers();
    }

    this.determineZipMaxLength();

    if (!this.templateValuesUSStates) {
      this.setStatesTemplateValues();
    }
    if (!this.templateValuesCountries) {
      this.setCountriesTemplateValues();
    }
    this.setDropdownOptions();
    if (this.data.values) {
      this.setFormValues();
    }
    this.setupRequiredFieldListeners();
    this.dialogRef.beforeClosed().subscribe(() => {
      if (!this.formHasValue && this.searchForm.dirty) {
        this.dialogRef.close({ clearAllFilters: true });
      }
    });
    this.determineStateOrProvince();

    // need to listen to value changes on the form to update formHasValue flag
    this.searchForm.valueChanges.subscribe(newVal => this.updateFormHasValue(newVal));
    this.updateFormHasValue(this.searchForm.value); // update for the initial state
  }

  determineZipMaxLength() {
    if (this.countryCode === 'AU') {
      this.maxlengthZipCode = 4;
    } else {
      this.maxlengthZipCode = 10;
    }
  }

  /**
   * Gets account managers list
   */
  getAccountManagers() {
    this.quoteDetailsService.getAccountManagersList().subscribe(list => {
      this.amList = [this.allAccountManagerOption, ...list];
      this.accountManagerOptions = this.utilsService.mapObjectToSelect(this.amList, 'employeeId', 'name');
      this.setDefaultAccountManager();
      this.cdr.detectChanges();
    });
  }

  /**
   * Sets the initial account manager value before the list loads
   */
  private setInitialAccountManagerValue() {
    const providedEmployeeId = this.resolveAccountManagerValue(this.data?.values?.accountManager);
    const storedEmployeeId = this.selectedAccountManagerService.selected?.employeeId;
    const employeeId = providedEmployeeId || storedEmployeeId || this.allAccountManagerOption.employeeId;

    this.createTemporaryAccountManagerOption(employeeId);
    this.searchForm.controls.accountManager.setValue(employeeId);
  }

  private createTemporaryAccountManagerOption(employeeId: string) {
    if (employeeId === this.allAccountManagerOption.employeeId) {
      this.accountManagerOptions = [{
        value: this.allAccountManagerOption.employeeId,
        label: this.allAccountManagerOption.name
      }];
    } else {
      const storedName = this.selectedAccountManagerService.selected?.name;
      if (storedName) {
        this.accountManagerOptions = [{ value: employeeId, label: storedName }];
      }
    }
  }

  /**
   * Setting default account manager - selected by admin AM or All
   */
  setDefaultAccountManager() {
    const providedSelection = this.data?.values?.accountManager;
    const providedEmployeeId = this.resolveAccountManagerValue(providedSelection);

    if (providedEmployeeId) {
      this.defaultAccountManager = this.getAccountManagerObject(providedEmployeeId) || this.allAccountManagerOption;
      return;
    }

    const storedAccountManager = this.selectedAccountManagerService.selected;
    if (storedAccountManager) {
      const storedManager = this.getAccountManagerObject(storedAccountManager.employeeId) || {
        id: storedAccountManager.employeeId,
        employeeId: storedAccountManager.employeeId,
        name: storedAccountManager.name || this.allAccountManagerOption.name,
        showOemSku: false
      };
      this.defaultAccountManager = storedManager;
      return;
    }

    this.defaultAccountManager = this.allAccountManagerOption;
  }

  /**
   * Updates the status of the formHasValue flag
   */
  private updateFormHasValue(newVal: any) {
    this.formHasValue = false;

    const comparableFields = ['state', 'country', 'filterByInterval', 'lnStatus', 'leads', 'accounts', 'delivery'];

    for (const [key, value] of Object.entries(newVal)) {
      if (key === 'accountManager') {
        if (value && value !== this.allAccountManagerOption.employeeId) {
          this.formHasValue = true;
          break;
        }
        continue;
      }

      if (comparableFields.includes(key)) {
        if (this.hasDifferentValue(key, value)) {
          this.formHasValue = true;
          break;
        }
        continue;
      }

      if (value) {
        this.formHasValue = true;
        break;
      }
    }
  }

  private hasDifferentValue(key: string, value: any) {
    const defaultValue = (this.formFields as any)[key];

    if (typeof defaultValue === 'boolean') {
      return value !== defaultValue;
    }

    if (key === 'state' || key === 'country' || key === 'lnStatus') {
      return Number(value || 0) !== Number(defaultValue || 0);
    }

    return value !== defaultValue;
  }

  private setupRequiredFieldListeners() {
    if (!this.requiredFilters?.length) {
      return;
    }

    this.requiredFilters.forEach(filter => {
      const control = this.searchForm.get(filter);
      control?.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(() => this.handleRequiredFilterChange(filter));
    });
  }

  private handleRequiredFilterChange(controlName: string) {
    if (this.suppressRequiredFilterToggle) {
      return;
    }
    this.toggleDisabledStateForTheOtherFields(controlName);
  }

  private setDropdownOptions() {
    if (this.templateValuesUSStates) {
      this.stateOptions = this.templateValuesUSStates.map(state => ({
        value: state.stateId,
        label: state.stateName
      }));
    }

    if (this.templateValuesCountries) {
      this.countryOptions = this.templateValuesCountries.map(country => ({
        value: country.Key,
        label: country.Value
      }));
    }
  }

  private resolveAccountManagerValue(accountManager: any): string {
    if (!accountManager) {
      return '';
    }

    if (typeof accountManager === 'string') {
      return accountManager;
    }

    return accountManager.employeeId;
  }

  /**
   * Sets the template values for states
   */
  private setStatesTemplateValues() {
    this.templateValuesUSStates = [...this.data.templateValuesUSStates];
    this.templateValuesUSStates.unshift(this.allStates);
    this.requiredFilters = Object.keys(this.data.FilterType).map(filter => this.data.FilterType[filter]);
    this.setDropdownOptions();
  }

  /**
   * Sets the template values for countries
   */
  private setCountriesTemplateValues() {
    this.templateValuesCountries = [...this.data.templateCountriesValues.Countries];
    this.templateValuesCountries.unshift(this.allCountries);
    this.requiredFilters = Object.keys(this.data.FilterType).map(filter => this.data.FilterType[filter]);
    this.setDropdownOptions();
  }

  /**
   * Sets the values in form, from parent component
   */
  private setFormValues() {
    const values = this.data.values;
    const filterType = this.data.FilterType;
    for (const value in values) {
      if (values.hasOwnProperty(value) && values[value] === null) {
        values[value] = '';
      }
    }
    this.searchForm.patchValue(values);
    this.searchForm.controls.state.setValue(values.stateId || 0);
    this.searchForm.controls.country.setValue(values.country || 0);
    this.searchForm.controls.accountManager.setValue(this.resolveAccountManagerValue(values.accountManager));
    if (this.searchForm.controls[filterType[values.searchBy]]) {
      this.searchForm.controls[filterType[values.searchBy]].setValue(values.searchField);
    }
    this.toggleDisabledStateForTheOtherFields(filterType[values.searchBy]);
  }

  /**
   * Triggers parent searchText() method with form values
   */
  searchText() {
    const formValue = { ...this.searchForm.value };
    const values = {
      searchFormValue: formValue,
      stateName: '',
      countryName: ''
    };

    const stateId = Number(this.searchForm.controls.state.value || 0);
    formValue.state = stateId;
    if (stateId) {
      values.stateName = this.templateValuesUSStates.find(state => state.stateId === stateId).stateName;
    } else {
      values.stateName = null;
    }

    const countryId = Number(this.searchForm.controls.country.value || 0);
    formValue.country = countryId;
    if (countryId) {
      values.countryName = this.templateValuesCountries.find(country => country.Key === countryId)?.Value;
    } else {
      values.countryName = null;
    }

    formValue.accountManager = this.getAccountManagerObject(formValue.accountManager);

    if (this.searchForm.dirty) {
      this.dialogRef.close(values);
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * Allows only one of the required filters to be populated
   * @param as currentControl
   */
  toggleDisabledStateForTheOtherFields(currentControl: string) {
    if (!currentControl || this.requiredFilters.indexOf(currentControl) === -1) {
      return;
    }

    const otherRequiredFilters = this.requiredFilters.filter(filter => filter !== currentControl);
    const hasValue = !!this.searchForm.controls[currentControl].value;

    this.suppressRequiredFilterToggle = true;
    try {
      for (const controlName of otherRequiredFilters) {
        const control = this.searchForm.controls[controlName];
        if (!control) {
          continue;
        }
        if (hasValue) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
      this.cdr.detectChanges();
    } finally {
      this.suppressRequiredFilterToggle = false;
    }
  }

  /**
   * Clears all values from the form
   */
  clearAllFilters() {
    Object.keys(this.searchForm.controls).forEach(key => this.searchForm.controls[key].enable());
    this.searchForm.markAsDirty();
    this.searchForm.setValue(this.formFields);
    // Ensure "All" option exists in the dropdown
    if (!this.accountManagerOptions.some(opt => opt.value === this.allAccountManagerOption.employeeId)) {
      this.accountManagerOptions.unshift({
        value: this.allAccountManagerOption.employeeId,
        label: this.allAccountManagerOption.name
      });
    }
    this.enableAllRequiredFilters();
  }

  private enableAllRequiredFilters() {
    this.suppressRequiredFilterToggle = true;
    try {
      this.requiredFilters.forEach(filter => this.searchForm.controls[filter]?.enable({ emitEvent: false }));
    } finally {
      this.suppressRequiredFilterToggle = false;
    }
  }

  /**
   * Determines whether the field should be named state or province, based on the country
   */
  determineStateOrProvince() {
    if (countriesWithProvince[this.countryCode]) {
      this.stateOrProvince = this.appStrings.paymentDetails_billing_province;
    } else {
      this.stateOrProvince = this.appStrings.paymentDetails_billing_state;
    }
  }

  private getAccountManagerObject(value: any): AccountManager {
    if (!value) {
      return null;
    }

    if (typeof value !== 'string') {
      return value;
    }

    return this.amList?.find(manager => manager.employeeId === value) ?? this.getUnlistedAccountManager(value);
  }

  private getUnlistedAccountManager(employeeId: string): AccountManager {
    const providedAccountManager = this.data?.values?.accountManager;
    if (providedAccountManager?.employeeId === employeeId) {
      return providedAccountManager;
    }

    const selectedAccountManager = this.selectedAccountManagerService.selected;
    return selectedAccountManager?.employeeId === employeeId
      ? { id: employeeId, employeeId, name: selectedAccountManager.name, showOemSku: false }
      : this.allAccountManagerOption;
  }

}
